import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardMockup from "@/components/landing/DashboardMockup";
import { useSiteContent } from "@/context/ContentContext";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export default function Hero() {
  const { hero } = useSiteContent();
  return (
    <section className="relative overflow-hidden pt-20 md:pt-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-[-160px] h-[320px]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(16,185,129,0.16), transparent 72%)",
        }}
      />

      <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 items-start gap-12 px-5 pb-16 sm:px-6 md:pb-24 lg:grid-cols-12 lg:gap-12 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-600/[0.07] px-3 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-emerald-700">
              {hero.eyebrow}
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            data-testid="hero-headline"
            className="mt-6 max-w-2xl text-4xl font-semibold leading-[1.03] tracking-[-0.04em] text-[color:var(--text-primary)] sm:text-5xl lg:text-6xl"
          >
            {hero.headline}{" "}
            <span className="text-emerald-600">{hero.headline_accent}</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-xl text-base leading-relaxed text-secondary md:text-lg"
          >
            {hero.subheadline}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-5 font-mono-tech text-xs tracking-wide text-tertiary"
          >
            {hero.techline}
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Button
              data-testid="hero-primary-cta-button"
              onClick={() => scrollTo("#contatto")}
              className="h-12 rounded-xl bg-emerald-700 px-6 text-[15px] font-medium text-white transition-colors duration-200 hover:bg-emerald-600"
            >
              {hero.cta_primary}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              data-testid="hero-secondary-cta-button"
              variant="outline"
              onClick={() => scrollTo("#problema")}
              className="h-12 rounded-xl border-black/15 bg-transparent px-6 text-[15px] font-medium text-[color:var(--text-primary)] transition-colors duration-200 hover:border-black/20 hover:bg-black/[0.04]"
            >
              {hero.cta_secondary}
            </Button>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={5}
            className="mt-6 flex items-center gap-2 text-sm text-tertiary"
          >
            <span className="h-px w-6 bg-emerald-400/50" />
            {hero.note}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
          className="lg:col-span-5"
        >
          <DashboardMockup />
        </motion.div>
      </div>

      <div className="mx-auto flex max-w-6xl justify-center px-5 pb-6 sm:px-6 lg:px-8">
        <button
          onClick={() => scrollTo("#problema")}
          aria-label="Scorri"
          className="flex items-center gap-2 text-xs text-tertiary transition-colors hover:text-secondary"
        >
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
          Scorri
        </button>
      </div>
    </section>
  );
}
